import { useTranslation } from 'react-i18next';
import { Settings, BarChart3, CreditCard, PanelLeftClose, PanelLeft, Lightbulb, Gauge, ClipboardCheck } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { UsageWidget } from '@/components/enforcement/UsageWidget';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { JobHealthIndicator } from '@/components/JobHealthIndicator';
import { useWorkbenchPendingCount } from '@/hooks/useWorkbench';
import { useSelection } from '@/contexts/SelectionContext';

/**
 * Ob ein Navigationseintrag zur aktuellen Route gehoert (A3-01). Die Wurzel
 * nur exakt, sonst erfuellte jede Seite "/". Unterpfade zaehlen mit
 * (`/settings/...`), Namensvettern nicht (`/settingsx`). Query-Strings
 * (`?tab=accounts`) stehen nicht im pathname und aendern nichts.
 */
export function isNavItemActive(pathname: string, url: string): boolean {
  if (url === '/') return pathname === '/';
  return pathname === url || pathname.startsWith(`${url}/`);
}

export function AppSidebar() {
  const { t } = useTranslation('common');
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  const collapsed = state === 'collapsed' && !isMobile;
  const { selectedFeedId } = useSelection();
  const { data: workbenchCount } = useWorkbenchPendingCount(selectedFeedId);

  const navGroups = [
    {
      // Drei Ziele, drei Flaechen (Task 13). `Start` und `Optimierungsscore`
      // beschrieben dieselbe Seite unter zwei Namen, `Automatisierung`
      // rendert exakt dieselbe Komponente wie der Einstellungs-Tab. Die
      // Reihenfolge ist der Arbeitsweg: Feed ansehen, Produkte pruefen,
      // Wirkung messen.
      label: t('nav.groups.workspace'),
      items: [
        { title: t('nav.feedCheck'), url: '/', icon: Gauge, badge: null },
        { title: t('nav.workbench'), url: '/workbench', icon: ClipboardCheck, badge: workbenchCount || null },
        { title: t('nav.performance'), url: '/performance', icon: BarChart3, badge: null },
      ],
    },
    {
      label: t('nav.groups.community'),
      items: [
        { title: t('nav.roadmap'), url: '/roadmap', icon: Lightbulb },
      ],
    },
    {
      label: t('nav.groups.account'),
      items: [
        { title: t('nav.plans'), url: '/pricing', icon: CreditCard },
        { title: t('nav.settings'), url: '/settings', icon: Settings },
      ],
    },
  ];

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="flex items-start p-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
          aria-label={t('toggleSidebar')}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            {!collapsed && (
              <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const active = isNavItemActive(pathname, item.url);
                  return (
                    <SidebarMenuItem key={item.title}>
                      {/*
                        `isActive` statt einer className-FUNKTION am NavLink:
                        SidebarMenuButton rendert mit `asChild` einen Radix-Slot,
                        der className als String verkettet. Die Funktion landete
                        so als Quelltext im class-Attribut, und data-active
                        blieb immer false (A3-01).
                      */}
                      <SidebarMenuButton asChild isActive={active} tooltip={collapsed ? item.title : undefined}>
                        <NavLink
                          to={item.url}
                          end={item.url === '/'}
                          // Eingeklappt steht nur das Icon da; der Tooltip ist kein
                          // zugaenglicher Name (K24).
                          aria-label={collapsed ? item.title : undefined}
                          // Mobil ist die Leiste ein Sheet ueber der Seite, das nach
                          // der Navigation sonst offen stehen bleibt (A3-02).
                          onClick={() => {
                            if (isMobile) setOpenMobile(false);
                          }}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          {!collapsed && <span className="text-sm">{item.title}</span>}
                          {!collapsed && item.badge && item.badge > 0 ? (
                            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          ) : null}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        {!collapsed && (
          <div className="space-y-3">
            <UsageWidget />
            <JobHealthIndicator />
            <div className="px-4 pb-2 text-xs text-muted-foreground">
              {t('copyright', { year: new Date().getFullYear().toString() })}
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
